<template>
  <div>
    <div class="d-flex flex-column flex-sm-row justify-space-between">
      <div>
        <a @click="lookupSchedule">
          {{ studentSchedule.studentFirstName }}
          {{ studentSchedule.studentLastName }}
        </a>
        <span v-if="!globalCenter || !globalCenter.id">
          @ {{ getCenterName(studentSchedule.centerId) }}
        </span>
      </div>
      <span>
        {{ studentSchedule.startTime | formatDate }}
        {{ studentSchedule.startTime | formatTime }} -
        {{ studentSchedule.endTime | formatTime }}
      </span>
    </div>
    <v-divider />
    <div
      v-if="studentSchedule.partOfRecurring"
      class="d-flex justify-space-between my-2"
    >
      <v-alert
        border="left"
        colored-border
        dense
        type="info"
        class="my-2 flex-grow-1"
      >
        {{
          recurringEdit
            ? "Editing recurring schedule"
            : "Editing an instance of a recurring schedule"
        }}
      </v-alert>
      <a class="my-auto" text @click="toggleEditMode">
        {{ recurringEdit ? "Edit Instance" : "Edit Recurring" }}
      </a>
    </div>
    <div
      v-if="isRecurring"
      class="d-flex flex-column flex-sm-row justify-space-between my-2"
    >
      <div class="text-caption">
        Weekly on {{ studentSchedule.recurringDays.join(", ") }}
      </div>
      <div class="text-caption">
        Since {{ studentSchedule.recurringStartDate | formatDate }}
        {{
          studentSchedule.recurringEndDate
            ? ` until ${$options.filters.formatDate(
                studentSchedule.recurringEndDate
              )}`
            : " (no end date)"
        }}
      </div>
    </div>

    <v-divider />

    <v-form class="mt-8" ref="form" v-model="valid">
      <div class="d-flex justify-space-between">
        <teacher-lookup
          class="flex-grow-1"
          v-if="!isClassroomAssignment"
          v-model="selectedTeacher"
          :required="false"
          @change="selectedClassroom = null"
          dense
          label="Assigned Teacher"
        />
        <span v-if="notAssigned" class="mx-4 my-auto"> or </span>
        <v-autocomplete
          class="flex-grow-1"
          v-if="!isTeacherAssignment"
          @change="selectedTeacher = null"
          :items="centerClassrooms"
          v-model="selectedClassroom"
          item-value="id"
          item-text="roomNumber"
          label="Assigned Classroom"
          dense
          clearable
        />
      </div>
      <v-autocomplete
        v-model="selectedProgram"
        :items="programs"
        item-value="id"
        item-text="name"
        label="Select Program"
        :rules="[rules.required]"
        required
        clearable
      />
      <date-picker
        :label="recurringEdit ? 'Start Date' : 'Date'"
        :defaultNowOnNull="false"
        :show-hint="false"
        :show-icon="false"
        v-model="selectedDate"
        :outlined="false"
        clearable
      />
      <v-text-field
        v-model="internalStartTime"
        label="Start Time*"
        type="time"
        :rules="[rules.required, validTimeRange]"
        required
      />
      <v-text-field
        v-model="internalEndTime"
        label="End Time*"
        type="time"
        :rules="[rules.required, validTimeRange]"
        required
      />
      <v-textarea v-model="notes" label="Notes" :rows="1" auto-grow />
      <div v-if="recurringEdit">
        <date-picker
          label="End Date (Optional)"
          :defaultNowOnNull="false"
          :show-hint="false"
          :show-icon="false"
          v-model="endDate"
          :outlined="false"
          clearable
        />
        <div class="d-flex justify-space-around">
          <v-checkbox
            v-for="(day, index) in daysOfWeek"
            :key="index"
            v-model="day.selected"
            :label="day.label"
          />
        </div>
      </div>

      <v-card-actions>
        <v-tooltip v-if="isAttendanceDataLoaded && isDeletable" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="error"
              icon
              v-bind="attrs"
              v-on="on"
              @click="onDeleteScheduleClick"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>
            Delete {{ recurringEdit ? "Recurring" : "This Instance" }}
          </span>
        </v-tooltip>

        <v-btn
          class="mx-auto"
          color="green darken-1"
          text
          :loading="isLoading"
          @click="updateSchedule"
        >
          Update Schedule
        </v-btn>
        <v-btn color="blue darken-1" text @click="onClose"> Close </v-btn>
      </v-card-actions>
    </v-form>
  </div>
</template>

<script>
import dialogMixin from "./dialogMixin";

import { mapGetters, mapActions } from "vuex";
const TeacherLookup = () => import("@/components/teacher/TeacherLookup.vue");
import {
  updateStudentScheduleOccurrence,
  fetchStudentScheduleInstances,
} from "@/services/schedulingService.js";
import AttendanceService from "@/services/attendanceService.js";
import { EventBus } from "@/EventBus.js";
import { openStudentScheduleLookup } from "@/components/schedule/eventHandler.js";

import moment from "moment";

export default {
  mixins: [dialogMixin],

  props: {
    studentSchedule: {
      type: Object,
      required: true,
    },
  },
  components: { TeacherLookup },
  data: () => ({
    selectedStudent: null,
    selectedProgram: null,
    selectedTeacher: null,
    selectedClassroom: null,
    isRecurring: false,
    recurringEdit: false,
    selectedDate: null,
    endDate: null,
    internalStartTime: null,
    internalEndTime: null,
    notes: null,
    programs: [],
    isAttendanceDataLoaded: false,
    isLoading: false,
    rules: {
      required: (value) => !!value || "Required.",
    },
    daysOfWeek: [
      { label: "Sun", selected: false },
      { label: "Mon", selected: false },
      { label: "Tue", selected: false },
      { label: "Wed", selected: false },
      { label: "Thu", selected: false },
      { label: "Fri", selected: false },
      { label: "Sat", selected: false },
    ],
  }),
  computed: {
    ...mapGetters(["globalCenter", "myCenters", "getCenterName"]),
    isDeletable() {
      // Only compute isDeletable if attendance data is loaded
      if (!this.isAttendanceDataLoaded) {
        return false;
      }

      const lastAttendedDate = this.getLastAttendedDate(
        this.attendanceData,
        this.studentSchedule.id
      );

      if (lastAttendedDate && this.studentSchedule.recurringEndDate) {
        // Parse the dates with moment.js for reliable comparison
        const scheduleEndDate = moment(this.studentSchedule.recurringEndDate);
        const lastAttended = moment(lastAttendedDate);

        // Compare the dates
        if (scheduleEndDate.isAfter(lastAttended, "day")) {
          return true;
        } else {
          return false;
        }
      } else {
        // If there is no attendance data or schedule end time, we consider the schedule as deletable
        return true;
      }
    },
    centerClassrooms() {
      if (this.studentSchedule && this.studentSchedule.centerId) {
        return this.classrooms.filter(
          (c) => c.centerId == this.studentSchedule.centerId
        );
      } else {
        return this.classrooms;
      }
    },
    isClassroomAssignment() {
      return this.selectedClassroom ? true : false;
    },
    isTeacherAssignment() {
      return this.selectedTeacher ? true : false;
    },
    notAssigned() {
      return !this.selectedTeacher && !this.selectedClassroom;
    },
  },
  watch: {
    selectedTeacher(newVal) {
      if (newVal) this.selectedClassroom = null;
      this.selectedTeacher = newVal;
    },
    selectedClassroom(newVal) {
      if (newVal) this.selectedTeacher = null;
      this.selectedClassroom = newVal;
    },
    startTime(newVal) {
      this.internalStartTime = newVal;
    },
    endTime(newVal) {
      this.internalEndTime = newVal;
    },
  },
  async mounted() {
    this.initSchedule();

    this.initAttendance();

    this.programs = await this.loadPrograms(this.studentSchedule.centerId);

    this.selectedProgram = this.programs.find(
      (p) => p.id == this.studentSchedule.programId
    );
  },
  methods: {
    ...mapActions(["addErrorMessage", "addSuccessMessage", "clearMessage"]),
    validTimeRange() {
      if (this.internalStartTime && this.internalEndTime) {
        return (
          this.internalEndTime > this.internalStartTime ||
          "End time cannot be earlier than start time."
        );
      }
      return true;
    },
    validateDaysOfWeek() {
      if (this.recurringEdit) {
        const atLeastOneDaySelected = this.daysOfWeek.some(
          (day) => day.selected
        );
        if (!atLeastOneDaySelected) {
          return "At least one day of the week must be selected for recurring schedules.";
        }
      }
      return true;
    },
    async loadPrograms(center) {
      const params = { centerId: center };
      return await this.$axios
        .get("/program/get-by-center", { params })
        .then((resp) => {
          return resp.data;
        });
    },
    initSchedule() {
      this.internalStartTime = moment(this.studentSchedule.startTime).format(
        "HH:mm"
      );
      this.internalEndTime = moment(this.studentSchedule.endTime).format(
        "HH:mm"
      );
      this.selectedDate = moment(this.studentSchedule.startTime).format(
        "YYYY-MM-DD"
      );

      const teacherId = this.studentSchedule.teacherId;
      this.selectedTeacher =
        teacherId && this.teachers
          ? this.teachers.find((t) => t.id == teacherId)
          : null;
      this.selectedClassroom = this.studentSchedule.classroomId;
      this.notes = this.studentSchedule.notes;
      this.isRecurring = this.studentSchedule.partOfRecurring;

      if (this.isRecurring) {
        this.daysOfWeek.forEach((dow) => {
          if (this.studentSchedule.recurringDays.includes(dow.label)) {
            dow.selected = true;
          }
        });
      }
    },
    async initAttendance() {
      let startDate = "1970-01-01"; // start date is set to earliest possible date
      let endDate = moment().format("YYYY-MM-DD"); // end date is set to today's date
      const attendance =
        await AttendanceService.fetchStudentAttendanceBySchedule(
          this.studentSchedule,
          startDate,
          endDate
        );

      this.attendanceData = attendance.data;
      this.isAttendanceDataLoaded = true;
    },
    getLastAttendedDate(attendanceRecords, scheduleId) {
      const dates = attendanceRecords
        .filter((attendance) => attendance.studentScheduleId === scheduleId)
        .map((a) => a.attendedDate);
      return dates.sort()[dates.length - 1];
    },
    async updateSchedule() {
      if (!this.$refs.form.validate()) {
        return;
      }

      // Custom validation for days of the week
      const daysOfWeekValid = this.validateDaysOfWeek();
      if (daysOfWeekValid !== true) {
        this.addErrorMessage(daysOfWeekValid);
        return;
      }

      this.isLoading = true;

      const studentScheduleId = this.studentSchedule.id;
      const originalDate = this.selectedDate;
      const newStartTime = moment(
        `${this.selectedDate}T${this.internalStartTime}`
      );
      const newEndTime = moment(`${this.selectedDate}T${this.internalEndTime}`);
      const newTeacherId = this.selectedTeacher
        ? this.selectedTeacher.id
        : null;
      const newClassroomId = this.selectedClassroom;
      const newProgramId = this.selectedProgram.id;
      if (this.recurringEdit) {
        const attendanceRecords = this.attendanceData;
        const lastAttendedDate = this.getLastAttendedDate(
          attendanceRecords,
          studentScheduleId
        );
        const lastAttendedDateMoment = lastAttendedDate
          ? moment(lastAttendedDate)
          : null;
        const recurringStartDateMoment = this.studentSchedule.recurringStartDate
          ? moment(this.studentSchedule.recurringStartDate)
          : null;

        if (
          lastAttendedDateMoment &&
          recurringStartDateMoment &&
          lastAttendedDateMoment.isAfter(recurringStartDateMoment)
        ) {
          if (
            window.confirm(
              `Attendance has been recorded for this recurring schedule. The last recorded attendance was on ${lastAttendedDate}. A new recurring schedule will be created after ${lastAttendedDate}. Proceed?`
            )
          ) {
            this.updateRecurringSchedule(
              newTeacherId,
              newClassroomId,
              newProgramId
            );
          } else {
            this.isLoading = false;
          }
        } else {
          this.updateRecurringSchedule(
            newTeacherId,
            newClassroomId,
            newProgramId
          );
        }
      } else {
        updateStudentScheduleOccurrence(
          studentScheduleId,
          originalDate,
          newStartTime,
          newEndTime,
          newTeacherId,
          newClassroomId,
          newProgramId
        )
          .then((updatedScheduleInstance) => {
            this.isLoading = false;
            this.addSuccessMessage("Schedule updated");

            this.onClose();
            EventBus.$emit("reload-chart");
            EventBus.$emit("reload-dialog", this.parentID, {
              action: "updated",
              originalSchedule: this.studentSchedule,
              schedule: updatedScheduleInstance,
            });
          })
          .catch(() => {
            this.isLoading = false;
            this.addErrorMessage("Error updating student schedule");
          });
      }
    },
    async updateRecurringSchedule(newTeacherId, newClassroomId, newProgramId) {
      try {
        // Prepare new schedule data
        const data = {
          centerId: this.studentSchedule.centerId,
          teacherId: newTeacherId,
          classroomId: newClassroomId,
          studentId: this.studentSchedule.studentId,
          programId: newProgramId,
          startTime: this.internalStartTime,
          endTime: this.internalEndTime,
          recurring: true,
          startDate: this.selectedDate,
          endDate: this.endDate, // Assuming endDate is already set in your component
          notes: this.notes,
          daysOfWeek: this.daysOfWeek
            .filter((dow) => dow.selected)
            .map((dow) => dow.label),
          timezone: new Date().getTimezoneOffset(),
        };

        // Create new recurring schedule from today's date
        const resp = await this.$axios.put(
          "/student-schedule/" + this.studentSchedule.id,
          data
        );
        const savedSchedule = resp.data;
        this.addSuccessMessage("Student schedule updated");

        //fetch new schedule's instances between the current dateRange
        const viewStartDate = moment(this.dateRange.start).format("YYYY-MM-DD");
        const viewEndDate = moment(this.dateRange.end).format("YYYY-MM-DD");
        const scheduleInstances = await fetchStudentScheduleInstances(
          this.studentSchedule.centerId,
          viewStartDate,
          viewEndDate,
          savedSchedule.id
        );

        this.addSuccessMessage("Recurring schedule updated");
        EventBus.$emit("reload-chart", {
          savedSchedule,
          teacherId: newTeacherId,
        });

        let updatedInstance = scheduleInstances.find((ss) =>
          ss.startTime.startsWith(this.selectedDate)
        );
        EventBus.$emit("reload-dialog", this.parentID, {
          action: "updated",
          originalSchedule: this.studentSchedule,
          schedule: updatedInstance,
        });
        this.onClose();

        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.addErrorMessage("Error updating recurring student schedule");
      }
    },
    onDeleteScheduleClick() {
      const schedule = this.studentSchedule;
      const attendanceRecords = this.attendanceData;

      if (attendanceRecords.length > 0) {
        if (this.recurringEdit) {
          // We have attendance records and it's a recurring schedule
          const lastAttendedDate = this.getLastAttendedDate(
            attendanceRecords,
            schedule.id
          );
          const lastAttendedDateMoment = lastAttendedDate
            ? moment(lastAttendedDate)
            : null;
          const recurringStartDateMoment = this.studentSchedule
            .recurringStartDate
            ? moment(this.studentSchedule.recurringStartDate)
            : null;
          if (
            lastAttendedDateMoment &&
            recurringStartDateMoment &&
            lastAttendedDateMoment.isAfter(recurringStartDateMoment)
          ) {
            // Alert the user and get confirmation
            if (
              window.confirm(
                `Attendance has been recorded for this recurring schedule. The last recorded attendance was on ${lastAttendedDate}. Would you want to set the end date to the last attended date?`
              )
            ) {
              // We can set the end date to the last attended date.
              schedule.endTime = lastAttendedDate;
              this.deleteSchedule(schedule);
              this.addSuccessMessage(
                "Schedule end date set to the last attended date."
              );
            }
          } else {
            // no last attendance data found, can delete whole recurring schedule
            this.deleteSchedule(schedule);
          }
        } else {
          // It's not a recurring schedule
          // Check if the attendance has been recorded on the same date as the schedule
          const scheduleDate = moment(schedule.startTime).format("YYYY-MM-DD");
          const isAttendanceTaken = attendanceRecords.some((attendance) => {
            const attendanceDate = moment(attendance.attendedDate).format(
              "YYYY-MM-DD"
            );
            return (
              attendanceDate === scheduleDate &&
              attendance.studentScheduleId === schedule.id
            );
          });
          if (isAttendanceTaken) {
            this.addErrorMessage(
              "Cannot delete the schedule. Attendance has already been taken on this date."
            );
          } else {
            this.deleteSchedule(schedule);
          }
        }
      } else {
        // No attendance records, regardless of recurring, allow to delete
        this.deleteSchedule(schedule);
      }
    },
    async deleteSchedule(schedule, emit = true) {
      const scheduleDate = moment(schedule.startTime).format("YYYY-MM-DD");

      // delete schedule if 'deleteRecurring' or a non-recurring schedule
      const params = {
        schedule_date:
          !schedule.partOfRecurring || this.recurringEdit ? null : scheduleDate,
      };
      await this.$axios
        .delete("/student-schedule/" + schedule.id, { params })
        .then(() => {
          // const event = {
          //   type: "deleted-student-schedule",
          //   data: schedule,
          //   teacherId: this.teacher ? this.teacher.id : null,
          // };

          if (emit) {
            this.addSuccessMessage("Schedule deleted");
            this.onClose();
            EventBus.$emit("reload-chart");
            EventBus.$emit("reload-dialog", this.parentID, {
              action: "deleted",
              schedule,
            });
          }
        })
        .catch((error) => {
          if (emit) {
            this.$emit("on-error", error);
          }
          this.addErrorMessage(
            error.message || "Error deleting student schedule"
          );
        });
    },
    toggleEditMode() {
      this.recurringEdit = !this.recurringEdit;
      const instanceDate = moment(this.studentSchedule.startTime).format(
        "YYYY-MM-DD"
      );

      if (this.recurringEdit) {
        this.selectedDate = this.studentSchedule.recurringStartDate
          ? this.studentSchedule.recurringStartDate
          : instanceDate;
      } else {
        this.selectedDate = instanceDate;
      }

      this.endDate = this.studentSchedule.recurringEndDate;
    },
    lookupSchedule() {
      const student = {
        id: this.studentSchedule.studentId,
        firstName: this.studentSchedule.studentFirstName,
        lastName: this.studentSchedule.studentLastName,
      };
      // const event = {
      //   type: "lookup-student-schedule",
      //   data: {
      //     student,
      //   },
      // };
      // this.$emit("on-success", event);
      openStudentScheduleLookup({ student });
    },

    isLastAttendedDateAfterRecurringStartDate() {
      // You might need to fetch the last attended date based on your logic
      const lastAttendedDate = this.getLastAttendedDate(
        this.attendanceData,
        this.studentSchedule.id
      );
      const lastAttendedDateMoment = lastAttendedDate
        ? moment(lastAttendedDate)
        : null;
      const recurringStartDateMoment = this.studentSchedule.recurringStartDate
        ? moment(this.studentSchedule.recurringStartDate)
        : null;

      return (
        lastAttendedDateMoment &&
        recurringStartDateMoment &&
        lastAttendedDateMoment.isAfter(recurringStartDateMoment)
      );
    },
  },
};
</script>
